import { useEffect, useMemo, useState, type FormEvent } from "react";
import { CalendarClock, X } from "lucide-react";
import { getSupabase } from "../lib/supabase";
import type { RepairRequest, RequestStatus, Urgency } from "../types";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { notifyRepairScheduled } from "../lib/whatsappApi";
import {
  contactPrefLabel,
  formatDateOnly,
  formatDatePt,
  periodLabel,
  statusLabel,
  cn,
} from "../lib/utils";

const statuses: Array<"todos" | RequestStatus> = [
  "todos",
  "pendente",
  "agendado",
  "em_curso",
  "concluido",
  "cancelado",
];

const urgencies: Array<"todas" | Urgency> = ["todas", "normal", "urgente"];

const fieldClass =
  "mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm text-primary";

export function AdminDashboard() {
  const [requests, setRequests] = useState<RepairRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<(typeof statuses)[number]>("todos");
  const [urgencyFilter, setUrgencyFilter] = useState<(typeof urgencies)[number]>("todas");
  const [selected, setSelected] = useState<RepairRequest | null>(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<"pedidos" | "agenda">("pedidos");

  const load = async () => {
    setError(null);
    const { data, error: queryError } = await getSupabase()
      .from("repair_requests")
      .select("*")
      .order("created_at", { ascending: false });
    if (queryError) {
      setError("Não foi possível carregar os pedidos.");
      setRequests([]);
    } else {
      setRequests((data ?? []) as RepairRequest[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(
    () =>
      requests.filter((item) => {
        const statusOk = statusFilter === "todos" || item.status === statusFilter;
        const urgencyOk = urgencyFilter === "todas" || item.urgencia === urgencyFilter;
        return statusOk && urgencyOk;
      }),
    [requests, statusFilter, urgencyFilter],
  );

  const agenda = useMemo(
    () =>
      requests
        .filter((item) => item.status === "agendado")
        .sort((a, b) => {
          const da = `${a.scheduled_date ?? ""} ${a.scheduled_time ?? ""}`;
          const db = `${b.scheduled_date ?? ""} ${b.scheduled_time ?? ""}`;
          return da.localeCompare(db);
        }),
    [requests],
  );

  const saveSelected = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected) return;
    setSaving(true);
    const form = new FormData(event.currentTarget);
    const patch = {
      status: String(form.get("status")) as RequestStatus,
      scheduled_date: (String(form.get("scheduled_date")) || null) as string | null,
      scheduled_time: (String(form.get("scheduled_time")) || null) as string | null,
      internal_notes: (String(form.get("internal_notes")) || null) as string | null,
    };
    const { error: updateError } = await getSupabase()
      .from("repair_requests")
      .update(patch)
      .eq("id", selected.id);
    setSaving(false);
    if (updateError) {
      setError("Não foi possível guardar as alterações.");
      return;
    }
    const becameScheduled = selected.status !== "agendado" && patch.status === "agendado";
    if (becameScheduled) {
      void notifyRepairScheduled({
        nome: selected.nome,
        telefone: selected.telefone,
        tipo_equipamento: selected.tipo_equipamento,
        scheduled_date: patch.scheduled_date,
        scheduled_time: patch.scheduled_time,
      });
    }
    setSelected(null);
    await load();
  };

  return (
    <div>
      <div className="mb-6 flex gap-2">
          <button
            type="button"
            className={cn(
              "rounded-lg border px-3 py-2 text-sm",
              tab === "pedidos" ? "border-primary text-primary" : "text-muted",
            )}
            onClick={() => setTab("pedidos")}
          >
            Pedidos
          </button>
          <button
            type="button"
            className={cn(
              "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm",
              tab === "agenda" ? "border-primary text-primary" : "text-muted",
            )}
            onClick={() => setTab("agenda")}
          >
            <CalendarClock className="h-4 w-4" aria-hidden />
            Agenda
          </button>
        </div>

        {error && <p className="mb-4 text-sm text-urgent">{error}</p>}

        {tab === "pedidos" && (
          <>
            <div className="mb-5 flex flex-wrap gap-4">
              <label className="text-sm text-muted">
                Estado
                <select
                  className={cn(fieldClass, "ml-2 w-auto")}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as (typeof statuses)[number])}
                >
                  {statuses.map((s) => (
                    <option key={s} value={s}>
                      {s === "todos" ? "Todos" : statusLabel(s)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm text-muted">
                Urgência
                <select
                  className={cn(fieldClass, "ml-2 w-auto")}
                  value={urgencyFilter}
                  onChange={(e) => setUrgencyFilter(e.target.value as (typeof urgencies)[number])}
                >
                  {urgencies.map((u) => (
                    <option key={u} value={u}>
                      {u === "todas" ? "Todas" : u === "urgente" ? "Urgente" : "Normal"}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {loading ? (
              <p className="text-muted">A carregar…</p>
            ) : filtered.length === 0 ? (
              <p className="text-muted">Sem pedidos neste filtro.</p>
            ) : (
              <ul className="space-y-3">
                {filtered.map((item) => (
                  <li key={item.id}>
                    <button type="button" className="w-full text-left" onClick={() => setSelected(item)}>
                      <Card hover className="grid gap-2 sm:grid-cols-[1fr_auto]">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-heading font-semibold text-primary">{item.nome}</span>
                            <Badge>{item.categoria_problema}</Badge>
                            {item.urgencia === "urgente" && <Badge tone="urgent">Urgente</Badge>}
                            <Badge tone="primary">{statusLabel(item.status)}</Badge>
                          </div>
                          <p className="mt-1 text-sm text-muted">
                            {item.telefone} · {item.email}
                          </p>
                          <p className="mt-2 line-clamp-2 text-sm text-muted">{item.descricao}</p>
                        </div>
                        <p className="text-xs text-muted">{formatDatePt(item.created_at)}</p>
                      </Card>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {tab === "agenda" && (
          <ul className="space-y-3">
            {agenda.length === 0 && <p className="text-muted">Ainda não há pedidos agendados.</p>}
            {agenda.map((item) => (
              <li key={item.id}>
                <button type="button" className="w-full text-left" onClick={() => setSelected(item)}>
                  <Card>
                    <p className="font-heading text-primary">
                      {formatDateOnly(item.scheduled_date)} {item.scheduled_time ?? ""}
                    </p>
                    <p className="mt-1 font-medium text-primary">{item.nome}</p>
                    <p className="text-sm text-muted">
                      {item.categoria_problema} · {item.telefone}
                    </p>
                  </Card>
                </button>
              </li>
            ))}
          </ul>
        )}

      {selected && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 sm:items-center">
          <form
            onSubmit={saveSelected}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border bg-surface p-6"
            aria-labelledby="pedido-titulo"
          >
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h2 id="pedido-titulo" className="font-heading text-xl font-semibold text-primary">
                  {selected.nome}
                </h2>
                <p className="text-sm text-muted">{formatDatePt(selected.created_at)}</p>
              </div>
              <button
                type="button"
                className="rounded-lg p-1 text-muted hover:text-primary"
                aria-label="Fechar detalhe"
                onClick={() => setSelected(null)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <dl className="grid gap-2 text-sm">
              <div>
                <dt className="text-muted">Contacto</dt>
                <dd className="text-primary">
                  {selected.telefone} · {selected.email} · {contactPrefLabel(selected.preferencia_contacto)}
                </dd>
              </div>
              <div>
                <dt className="text-muted">Equipamento / problema</dt>
                <dd className="text-primary">
                  {selected.tipo_equipamento} · {selected.categoria_problema}
                  {selected.urgencia === "urgente" ? " · Urgente" : ""}
                </dd>
              </div>
              <div>
                <dt className="text-muted">Descrição</dt>
                <dd className="text-primary">{selected.descricao}</dd>
              </div>
              <div>
                <dt className="text-muted">Disponibilidade pedida</dt>
                <dd className="text-primary">
                  {formatDateOnly(selected.disponibilidade_data)} · {periodLabel(selected.disponibilidade_periodo)}
                </dd>
              </div>
            </dl>

            <label className="mt-5 block text-sm text-primary">
              Estado
              <select name="status" className={fieldClass} defaultValue={selected.status}>
                {statuses
                  .filter((s): s is RequestStatus => s !== "todos")
                  .map((s) => (
                    <option key={s} value={s}>
                      {statusLabel(s)}
                    </option>
                  ))}
              </select>
            </label>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="text-sm text-primary">
                Data de agendamento
                <input
                  type="date"
                  name="scheduled_date"
                  className={fieldClass}
                  defaultValue={selected.scheduled_date ?? ""}
                />
              </label>
              <label className="text-sm text-primary">
                Hora
                <input
                  type="time"
                  name="scheduled_time"
                  className={fieldClass}
                  defaultValue={selected.scheduled_time ?? ""}
                />
              </label>
            </div>
            <label className="mt-3 block text-sm text-primary">
              Notas internas
              <textarea
                name="internal_notes"
                rows={4}
                className={fieldClass}
                defaultValue={selected.internal_notes ?? ""}
              />
            </label>
            <Button type="submit" className="mt-5" disabled={saving}>
              {saving ? "A guardar…" : "Guardar alterações"}
            </Button>
          </form>
        </div>
      )}
    </div>
  );
}
